import PropTypes from 'prop-types';
import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { asyncConnect } from 'redux-connect';
import { setSuccessMessage } from 'redux/modules/successMessage';
import { setErrorMessage } from 'redux/modules/errorMessage';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Grid, Row } from 'react-bootstrap';
import format from 'date-fns/format';
import Fuse from 'fuse.js';
import { ArrowLeft } from 'lucide-react';

import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import ApplicantView from 'components/ClientATS/ApplicantView';
import ApplicantList from 'components/ClientATS/ApplicantList';
import NoApplicantsEmptyState from 'components/ClientATS/NoApplicantsEmptyState';
import EditJobModal from 'components/ClientATS/ModifyJobModals/EditJobModal';
import DuplicateJobModal from 'components/ClientATS/ModifyJobModals/DuplicateJobModal';
import PublicQuestionsModal from 'components/ClientATS/PublicQuestionsModal';
import PaymentModal from 'components/Pitch/PaymentModal';
import NextStepsModal from 'components/ClientATS/NextStepsModal/NextStepsModal';
import JobHeader from 'components/ClientATS/JobHeader/JobHeader';
import BulkActionBar from 'components/ClientATS/BulkActionBar';
import BulkMessageModal from 'components/ClientATS/BulkMessageModal';
import BulkConfirmationModal from 'components/ClientATS/BulkConfirmationModal';
import JobActionCards from 'components/ClientATS/JobActionCards/JobActionCards';
import CloseJobModal from 'components/ClientATS/CloseJobModal/CloseJobModal';
import ReopenJobModal from 'components/ClientATS/ReopenJobModal/ReopenJobModal';
import ApplicantTabs from 'components/ClientATS/ApplicantTabs/ApplicantTabs';
import Walkthrough from 'components/ClientATS/Walkthrough/Walkthrough';

import { getProfileCompletion } from 'helpers/profileCompletion';
import { trackEvent, reloadAuthAndTrackHireSuccess } from 'helpers/Analytics';

import {
  loadBrief,
  loadPitches,
  refreshPitches,
  markPitchAsRead,
  markPitchThreadAsRead,
  declinePitch,
  shortlistPitch,
  loadPitchMessages,
  sendPitchMessage,
  sendPitchFile,
  sendBulkMessages,
  bulkShortlistPitches,
  bulkDeclinePitches,
  bulkReviewPitches,
  bulkResetPitches,
  endHire,
  resetPitch,
  acceptPitch,
  loadPublicQuestions,
  createBrief,
  updateBrief,
  reviewPitch,
  loadPitchPayments,
  loadInternalNotes,
  loadApplicantFeaturedProjects,
} from 'redux/modules/clientATS';
import { updateTour } from 'redux/modules/auth';
import { getBrowseURL } from 'helpers/HelperFunctions';
import {
  ATS_POST_APPLICATIONS_TOUR,
  ATS_PRE_APPLICATIONS_TOUR,
  atsApplicationReviewWalkthroughSteps,
  atsJobActionsWalkthroughSteps,
} from 'helpers/ATSWalkthroughSteps';

import styles from './JobDetails.module.scss';

const APPLICATION_REVIEW_STEP_IDS = atsApplicationReviewWalkthroughSteps.map(
  (step) => step.id
);

const compareApplicantsByTime = (sortOrder) => (a, b) => {
  const aTime = a.timeAgo ? new Date(a.timeAgo).getTime() : 0;
  const bTime = b.timeAgo ? new Date(b.timeAgo).getTime() : 0;

  if (sortOrder === 'newest') {
    return bTime - aTime;
  }

  return aTime - bTime;
};

const JobDetails = ({ match, location }) => {
  // Initialize activeTab from query parameter if it exists
  const getInitialTab = () => {
    const queryParams = new URLSearchParams(location?.search || '');
    const tabFromQuery = queryParams.get('tab');
    const validTabs = [
      'applied',
      'reviewed',
      'shortlisted',
      'declined',
      'hired',
      'invited',
      'all',
    ];
    if (tabFromQuery && validTabs.includes(tabFromQuery)) {
      return tabFromQuery;
    }
    return 'applied';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [showJobMenu, setShowJobMenu] = useState(false);
  const [showHireModal, setShowHireModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [savingBriefRequest, setSavingBriefRequest] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showCloseJobModal, setShowCloseJobModal] = useState(false);
  const [closingJob, setClosingJob] = useState(false);
  const [showReopenJobModal, setShowReopenJobModal] = useState(false);
  const [reopeningJob, setReopeningJob] = useState(false);
  const [aiRecruiterEnabled, setAiRecruiterEnabled] = useState(null);
  const [isPrivate, setIsPrivate] = useState(null);
  const [showNextStepsModal, setShowNextStepsModal] = useState(false);
  const [selectedApplicantIds, setSelectedApplicantIds] = useState([]);
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [showAtsWalkthrough, setShowAtsWalkthrough] = useState(false);
  const [atsWalkthroughManuallyOpened, setAtsWalkthroughManuallyOpened] =
    useState(false);
  const [atsWalkthroughDismissed, setAtsWalkthroughDismissed] = useState(false);
  const [walkthroughActiveStepId, setWalkthroughActiveStepId] = useState(null);

  //Filters
  const [hasChangedFilter, setHasChangedFilter] = useState(false);
  const [unreadFilter, setUnreadFilter] = useState(false); // false = 'all' | true = 'unread'
  const [unreadFilterApplicantIds, setUnreadFilterApplicantIds] = useState([]); // used to track the ids of the applicants that match the unread filter when filter is first applied
  const [searchQuery, setSearchQuery] = useState('');
  const [applicantFilters, setApplicantFilters] = useState({
    sortOrder: 'newest',
    selectedLocations: [], // Array of selected location strings for filtering
    selectedSkills: [], // Array of selected skill names for filtering
  });

  const tabRefs = useRef({});
  const hasAppliedDeepLinkSelection = useRef(false);
  const hasPreparedDeepLinkSelection = useRef(false);
  const hasPreparedApplicationWalkthrough = useRef(false);
  const hasTrackedAtsWalkthroughStart = useRef(false);
  const dispatch = useDispatch();
  const history = useHistory();

  const auth = useSelector((state) => state.auth);
  const hasCompletedAtsPreApplicationsTour =
    !!auth.user?.tour?.[ATS_PRE_APPLICATIONS_TOUR];
  const hasCompletedAtsPostApplicationsTour =
    !!auth.user?.tour?.[ATS_POST_APPLICATIONS_TOUR];
  const isAdminViewingAsUser = typeof auth.user?.admin_user_id !== 'undefined';

  const {
    brief,
    loading,
    pitches,
    pitchesLoadedForBriefId,
    loadingPitches,
    pitchesLoaded,
    decliningPitch,
    shortlistingPitch,
    sendingBulkMessages,
    bulkShortlistingPitches,
    bulkDecliningPitches,
    bulkReviewingPitches,
    bulkResettingPitches,
    reviewingPitch,
    endingHire,
    resettingPitch,
    acceptingPitch,
    pitchMessages,
    pitchMessagesMeta,
    loadingPitchMessages,
    markingThreadAsRead,
    markedThreads,
    publicQuestions,
    publicQuestionsLoadedForBriefId,
    loadingPitchPayments,
    pitchPayments,
    pitchPaymentsStats,
    internalNotes,
    loadingInternalNotes,
  } = useSelector((state) => state.clientATS);

  // Set isDesktop on mount and handle window resize
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    // Set initial value
    checkIsDesktop();

    // Add resize listener
    window.addEventListener('resize', checkIsDesktop);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIsDesktop);
    };
  }, []);

  const encodedId = useMemo(() => {
    return match.params.id ? match.params.id.split('-')[0] : null;
  }, [match.params.id]);

  const currentBriefId = brief?.id;
  const selectedApplicantUserIdFromQuery = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search || '');
    const applicantUserId = queryParams.get('selectedApplicant');
    return applicantUserId ? applicantUserId.trim() : '';
  }, [location?.search]);

  const tabFromQuery = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search || '');
    const tab = queryParams.get('tab');
    return tab ? tab.trim() : '';
  }, [location?.search]);

  // applicantTab supports deep-linking to a sub-tab within an applicant's view (e.g. "payments")
  const applicantTabFromQuery = useMemo(() => {
    const queryParams = new URLSearchParams(location?.search || '');
    const tab = queryParams.get('applicantTab');
    return tab ? tab.trim() : '';
  }, [location?.search]);

  useEffect(() => {
    hasAppliedDeepLinkSelection.current = false;
    hasPreparedDeepLinkSelection.current = false;
  }, [encodedId, selectedApplicantUserIdFromQuery, tabFromQuery]);

  // Load brief if not loaded or if ID doesn't match
  useEffect(() => {
    if (encodedId && (!currentBriefId || currentBriefId !== encodedId)) {
      if (!loading) {
        dispatch(loadBrief(encodedId));
      }
    }
  }, [encodedId, dispatch]);

  // Load pitches when brief is loaded or when brief ID changes
  useEffect(() => {
    const briefUserId = brief?.user?.id;
    const authUserId = auth?.user?.id;

    if (
      currentBriefId &&
      authUserId === briefUserId &&
      !loadingPitches &&
      pitchesLoadedForBriefId !== currentBriefId
    ) {
      dispatch(loadPitches(currentBriefId));
    }
  }, [
    currentBriefId,
    brief?.user?.id,
    auth?.user?.id,
    loadingPitches,
    pitchesLoadedForBriefId,
    dispatch,
  ]);

  // Reload pitches when returning from payment page (check for URL params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasPaymentSuccess = params.has('payment-successful');
    const hasPaymentCancelled = params.has('payment-cancelled');

    if (hasPaymentSuccess && brief?.id && pitchesLoaded) {
      // Show success message
      dispatch(
        setSuccessMessage('Your payment has been processed successfully')
      );

      if (params.get('hire') === '1') {
        dispatch(
          reloadAuthAndTrackHireSuccess({
            briefId: brief.id,
          })
        );
      }

      // Reload pitches to get updated state after payment
      dispatch(loadPitches(brief.id));
    } else if (hasPaymentCancelled && brief?.id && pitchesLoaded) {
      dispatch(setErrorMessage('Payment was cancelled'));
    }

    if (
      (hasPaymentSuccess || hasPaymentCancelled) &&
      brief?.id &&
      pitchesLoaded
    ) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [brief, pitchesLoaded, dispatch]);

  // Default to 'invited' tab if there are no applicants in the 'applied' tab (only on initial load)
  const hasCheckedInitialTab = useRef(false);
  useEffect(() => {
    if (pitchesLoaded && !hasCheckedInitialTab.current) {
      hasCheckedInitialTab.current = true;

      // If tab was specified in URL or selectedApplicant deeplink exists, don't change the tab
      if (tabFromQuery || selectedApplicantUserIdFromQuery) {
        return;
      }
      // Check if there are any pitches with 'applied' stage
      const hasAppliedPitches = pitches.some((pitch) => {
        return (
          pitch.approved_at &&
          !pitch.accepted_at &&
          !pitch.completed_at &&
          !pitch.declined_at &&
          !pitch.liked_at
        );
      });
      if (!hasAppliedPitches) {
        setActiveTab('invited');
      }
    }
  }, [pitchesLoaded, pitches, selectedApplicantUserIdFromQuery, tabFromQuery]);

  // Load public questions when brief is loaded or when brief ID changes
  useEffect(() => {
    if (currentBriefId && publicQuestionsLoadedForBriefId !== currentBriefId) {
      dispatch(loadPublicQuestions(currentBriefId));
    }
  }, [currentBriefId, publicQuestionsLoadedForBriefId, dispatch]);

  // Helper function to transform user data to applicant format
  const transformUserToApplicant = (
    user,
    stage,
    timeAgo,
    isUnread = false,
    pitchId = null,
    hasUnreadMessages = false
  ) => {
    // Handle location - extract country, city, and text
    let locationText = 'Location not specified';
    let locationCountry = null;
    let locationCity = null;

    if (user.location) {
      if (typeof user.location === 'string') {
        locationText = user.location;
      } else if (typeof user.location === 'object') {
        locationText = user.location.text || 'Location not specified';
        locationCountry = user.location.country || null;
        locationCity = user.location.city || null;
      }
    }

    // Get roles
    const roles = user.featured_roles || user.top_roles || [];

    return {
      id: pitchId || user.id,
      userId: user.id,
      name: user.displayname || user.username || 'Unknown',
      avatars: user.avatars,
      location: locationText,
      locationCountry: locationCountry,
      locationCity: locationCity,
      roles: roles.slice(0, 3),
      skills: user.skills,
      stage: stage,
      timeAgo: timeAgo,
      isUnread: isUnread,
      hasUnreadMessages: hasUnreadMessages,
      user: user, // Include full user object for normalized access
    };
  };

  // Transform invited users from brief
  const invitedApplicants = useMemo(() => {
    const invites = [];
    if (brief && brief.invites && brief.invites.invites) {
      // Get set of user IDs who have already applied (have pitches)
      const appliedUserIds = new Set(
        pitches.map((pitch) => pitch.sender?.id).filter(Boolean)
      );

      brief.invites.invites.forEach((invite) => {
        // Only include invited users who haven't applied yet
        if (!appliedUserIds.has(invite.user?.id)) {
          invites.push(
            transformUserToApplicant(
              invite.user,
              'invited',
              invite.created_at,
              false,
              `invite-${invite.id}`
            )
          );
        }
      });
    }

    return invites;
  }, [brief, pitches]);

  // Transform pitches data to applicant format
  const pitchApplicants = useMemo(() => {
    return pitches.map((pitch) => {
      const sender = pitch.sender || {};

      // Determine stage based on pitch status
      // Priority order: hired > declined > shortlisted > reviewed > applied
      let stage = 'applied';
      if (pitch.completed_at || pitch.accepted_at) {
        stage = 'hired';
      } else if (pitch.declined_at) {
        stage = 'declined';
      } else if (pitch.liked_at) {
        stage = 'shortlisted';
      } else if (pitch.reviewed_at) {
        stage = 'reviewed';
      } else if (pitch.approved_at) {
        stage = 'applied';
      }

      return transformUserToApplicant(
        sender,
        stage,
        pitch.timestamp,
        !pitch.receiver_viewed,
        pitch.id,
        pitch.has_unread_messages
      );
    });
  }, [pitches]);

  const firstAppliedApplicant = useMemo(() => {
    const sortOrder = applicantFilters?.sortOrder || 'newest';
    const appliedApplicants = pitchApplicants.filter(
      (applicant) => applicant.stage === 'applied'
    );

    appliedApplicants.sort(compareApplicantsByTime(sortOrder));

    return appliedApplicants[0];
  }, [applicantFilters?.sortOrder, pitchApplicants]);

  const hasAppliedApplicants = !!firstAppliedApplicant;

  const activeAtsTourFields = useMemo(() => {
    const tourFields = [];

    if (!hasCompletedAtsPreApplicationsTour) {
      tourFields.push(ATS_PRE_APPLICATIONS_TOUR);
    }

    if (hasAppliedApplicants && !hasCompletedAtsPostApplicationsTour) {
      tourFields.push(ATS_POST_APPLICATIONS_TOUR);
    }

    return tourFields;
  }, [
    hasAppliedApplicants,
    hasCompletedAtsPostApplicationsTour,
    hasCompletedAtsPreApplicationsTour,
  ]);

  const activeAtsWalkthroughSteps = useMemo(() => {
    return activeAtsTourFields.reduce((steps, tourField) => {
      if (tourField === ATS_PRE_APPLICATIONS_TOUR) {
        return steps.concat(atsJobActionsWalkthroughSteps);
      }

      if (tourField === ATS_POST_APPLICATIONS_TOUR) {
        return steps.concat(atsApplicationReviewWalkthroughSteps);
      }

      return steps;
    }, []);
  }, [activeAtsTourFields]);

  const manuallyOpenedAtsWalkthroughSteps = useMemo(
    () =>
      hasAppliedApplicants
        ? atsJobActionsWalkthroughSteps.concat(
            atsApplicationReviewWalkthroughSteps
          )
        : atsJobActionsWalkthroughSteps,
    [hasAppliedApplicants]
  );

  const displayedAtsWalkthroughSteps = atsWalkthroughManuallyOpened
    ? manuallyOpenedAtsWalkthroughSteps
    : activeAtsWalkthroughSteps;

  const displayedAtsTourFields = useMemo(
    () =>
      atsWalkthroughManuallyOpened
        ? [
            ATS_PRE_APPLICATIONS_TOUR,
            ...(hasAppliedApplicants ? [ATS_POST_APPLICATIONS_TOUR] : []),
          ]
        : activeAtsTourFields,
    [activeAtsTourFields, atsWalkthroughManuallyOpened, hasAppliedApplicants]
  );

  const getAtsWalkthroughTrackingProperties = useCallback(
    () => ({
      briefId: brief?.id,
      isManual: atsWalkthroughManuallyOpened,
      stepCount: displayedAtsWalkthroughSteps.length,
      tourFields: displayedAtsTourFields,
    }),
    [
      atsWalkthroughManuallyOpened,
      brief?.id,
      displayedAtsTourFields,
      displayedAtsWalkthroughSteps.length,
    ]
  );

  // Apply all filters (tab, unread, location) in one step
  const filteredApplicants = useMemo(() => {
    // Start with the right set based on active tab
    let applicants =
      activeTab === 'invited'
        ? invitedApplicants
        : pitchApplicants.filter((applicant) => applicant.stage === activeTab);

    // Apply unread filter if active
    if (activeTab === 'applied' && unreadFilter) {
      applicants = applicants.filter((applicant) =>
        unreadFilterApplicantIds.includes(applicant.id)
      );
    }

    // Apply location filter if locations are selected
    const selectedLocations = applicantFilters?.selectedLocations || [];
    if (selectedLocations.length > 0) {
      // Build country -> cities map to check "all cities selected"
      const countryToCities = {};
      applicants.forEach((a) => {
        if (a.locationCountry) {
          if (!countryToCities[a.locationCountry]) {
            countryToCities[a.locationCountry] = new Set();
          }
          if (a.locationCity) {
            countryToCities[a.locationCountry].add(a.locationCity);
          }
        }
      });

      const countryHasAllCitiesSelected = (country) => {
        const cities = countryToCities[country];
        if (!cities || cities.size === 0) {
          return selectedLocations.includes(country);
        }
        return (
          selectedLocations.includes(country) &&
          [...cities].every((city) => selectedLocations.includes(city))
        );
      };

      applicants = applicants.filter((applicant) => {
        // Applicants with a city: require city match
        if (applicant.locationCity) {
          return selectedLocations.includes(applicant.locationCity);
        }
        // Applicants without a city: only include if country has all its cities selected
        if (applicant.locationCountry) {
          return countryHasAllCitiesSelected(applicant.locationCountry);
        }
        // Fallback: check full location text for legacy data (no country/city structure)
        return selectedLocations.includes(applicant.location);
      });
    }

    // Apply skills filter if skills are selected (applicant must have all selected skills)
    const selectedSkills = applicantFilters?.selectedSkills || [];
    if (selectedSkills.length > 0) {
      const getSkillName = (s) => (typeof s === 'string' ? s : s?.name);
      applicants = applicants.filter((applicant) => {
        const applicantSkills = applicant.skills || [];
        const applicantSkillNames = applicantSkills
          .map(getSkillName)
          .filter(Boolean);
        return selectedSkills.every((selected) =>
          applicantSkillNames.includes(selected)
        );
      });
    }

    return applicants;
  }, [
    activeTab,
    invitedApplicants,
    pitchApplicants,
    unreadFilter,
    unreadFilterApplicantIds,
    applicantFilters?.selectedLocations,
    applicantFilters?.selectedSkills,
  ]);

  // Extract hierarchical location data grouped by country
  const availableLocations = useMemo(() => {
    // Get locations from tab-filtered applicants (before location filter is applied)
    const tabFiltered =
      activeTab === 'invited'
        ? invitedApplicants
        : pitchApplicants.filter((applicant) => applicant.stage === activeTab);

    // Group locations by country
    const locationsByCountry = {};
    let unspecifiedCount = 0;

    tabFiltered.forEach((applicant) => {
      const country = applicant.locationCountry;
      const city = applicant.locationCity;
      const text = applicant.location;

      // Track if there are applicants with unspecified locations
      if (!text || text === 'Location not specified') {
        unspecifiedCount += 1;
        return;
      }

      // If we have country/city structure, use it
      if (country) {
        if (!locationsByCountry[country]) {
          locationsByCountry[country] = {
            country: country,
            cities: {}, // map city name -> applicant count
            hasCountryOnly: false, // Track if there are country-only entries
            count: 0,
          };
        }
        locationsByCountry[country].count += 1;
        if (city) {
          if (!locationsByCountry[country].cities[city]) {
            locationsByCountry[country].cities[city] = 0;
          }
          locationsByCountry[country].cities[city] += 1;
        } else {
          // User has country but no city
          locationsByCountry[country].hasCountryOnly = true;
        }
      } else {
        // Fallback: treat full text as country if no structured data
        if (!locationsByCountry[text]) {
          locationsByCountry[text] = {
            country: text,
            cities: {},
            hasCountryOnly: true, // Legacy data is treated as country-only
            count: 0,
          };
        }
        locationsByCountry[text].count += 1;
      }
    });

    // Convert to array format and sort
    const locations = Object.values(locationsByCountry)
      .map((loc) => ({
        country: loc.country,
        cities: Object.keys(loc.cities)
          .sort((a, b) => a.localeCompare(b))
          .map((name) => ({ name, count: loc.cities[name] })),
        hasCountryOnly: loc.hasCountryOnly,
        count: loc.count,
      }))
      .sort((a, b) => a.country.localeCompare(b.country));

    // Add "Location not specified" at the end if there are any
    if (unspecifiedCount > 0) {
      locations.push({
        country: 'Location not specified',
        cities: [],
        count: unspecifiedCount,
      });
    }

    return locations;
  }, [activeTab, invitedApplicants, pitchApplicants]);

  // Extract unique skills from tab-filtered applicants (for filter checkboxes)
  const availableSkills = useMemo(() => {
    const tabFiltered =
      activeTab === 'invited'
        ? invitedApplicants
        : pitchApplicants.filter((applicant) => applicant.stage === activeTab);
    const getSkillName = (s) => (typeof s === 'string' ? s : s?.name);
    const skillCounts = {};

    tabFiltered.forEach((applicant) => {
      (applicant.skills || []).forEach((skill) => {
        const name = getSkillName(skill);
        if (!name) return;
        if (!skillCounts[name]) {
          skillCounts[name] = 0;
        }
        skillCounts[name] += 1;
      });
    });

    return Object.keys(skillCounts)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ name, count: skillCounts[name] }));
  }, [activeTab, invitedApplicants, pitchApplicants]);

  // Initialize Fuse.js with filtered applicants
  const fuse = useMemo(() => {
    return new Fuse(filteredApplicants, {
      keys: ['name', 'location', 'roles', 'skills.name'],
      threshold: 0.2,
      includeScore: true,
      minMatchCharLength: 2,
    });
  }, [filteredApplicants]);

  // Apply search filter to filtered applicants
  const searchedApplicants = useMemo(() => {
    if (!searchQuery) {
      return filteredApplicants;
    }
    return fuse.search(searchQuery).map((result) => result.item);
  }, [filteredApplicants, searchQuery, fuse]);

  // Apply sort order last to searched applicants
  const filteredAndSortedApplicants = useMemo(() => {
    const sortOrder = applicantFilters?.sortOrder || 'newest';
    const applicants = [...searchedApplicants];

    applicants.sort(compareApplicantsByTime(sortOrder));

    return applicants;
  }, [searchedApplicants, applicantFilters?.sortOrder]);

  useEffect(() => {
    if (
      !selectedApplicantUserIdFromQuery ||
      hasAppliedDeepLinkSelection.current
    ) {
      return;
    }

    if (!pitchesLoaded || loadingPitches) {
      return;
    }

    const allApplicants = [...pitchApplicants, ...invitedApplicants];
    const deepLinkedApplicant = allApplicants.find(
      (applicant) =>
        String(applicant.userId) === String(selectedApplicantUserIdFromQuery)
    );

    if (!deepLinkedApplicant) {
      hasAppliedDeepLinkSelection.current = true;
      return;
    }

    if (!hasPreparedDeepLinkSelection.current) {
      // Reset list controls so the deep-linked applicant stays visible.
      setHasChangedFilter(false);
      setUnreadFilter(false);
      setUnreadFilterApplicantIds([]);
      setSearchQuery('');
      setSelectedApplicantIds([]);
      setApplicantFilters({
        sortOrder: 'newest',
        selectedLocations: [],
        selectedSkills: [],
      });
      hasPreparedDeepLinkSelection.current = true;
    }

    if (activeTab !== deepLinkedApplicant.stage) {
      setActiveTab(deepLinkedApplicant.stage);
      return;
    }

    const deepLinkedApplicantInTab = filteredAndSortedApplicants.find(
      (applicant) =>
        String(applicant.userId) === String(selectedApplicantUserIdFromQuery)
    );

    if (!deepLinkedApplicantInTab) {
      return;
    }

    setSelectedApplicant(deepLinkedApplicantInTab);
    setShowMobileDetail(true);

    if (deepLinkedApplicantInTab.isUnread) {
      dispatch(markPitchAsRead(deepLinkedApplicantInTab.id));
    }

    hasAppliedDeepLinkSelection.current = true;
  }, [
    selectedApplicantUserIdFromQuery,
    pitchesLoaded,
    loadingPitches,
    pitchApplicants,
    invitedApplicants,
    activeTab,
    filteredAndSortedApplicants,
    dispatch,
  ]);

  // Track applicants that match the unread filter without removing them when they become read
  useEffect(() => {
    if (!unreadFilter) {
      return;
    }

    // Find all applicants that match the unread filter
    const newlyMatchingIds = pitchApplicants
      .filter(
        (applicant) =>
          applicant.stage === 'applied' &&
          (applicant.isUnread || applicant.hasUnreadMessages)
      )
      .map((applicant) => applicant.id);

    // If no applicants match the unread filter, return
    if (newlyMatchingIds.length === 0) {
      return;
    }

    // Update the unread filter applicant ids if there are any changes
    setUnreadFilterApplicantIds((prevIds) => {
      const idSet = new Set(prevIds);
      let hasChange = false;

      newlyMatchingIds.forEach((id) => {
        if (!idSet.has(id)) {
          idSet.add(id);
          hasChange = true;
        }
      });

      return hasChange ? Array.from(idSet) : prevIds;
    });
  }, [pitchApplicants, unreadFilter]);

  // Keep selection in sync with current tab/applicants and auto-select first applicant
  useEffect(() => {
    // Don't auto-select or clear selection while hire modal is open
    // This prevents clearing selectedApplicant when the hired user moves to a different tab
    if (showHireModal) {
      return;
    }

    // Don't auto-select if deeplink query parameter exists
    // The deeplink logic handles selection when there's a selectedApplicant in the URL
    if (selectedApplicantUserIdFromQuery) {
      return;
    }

    if (filteredAndSortedApplicants.length === 0) {
      // Clear selection if no applicants in current tab
      setSelectedApplicant(null);
      return;
    }

    // If the currently selected applicant is no longer in the filtered list, clear it.
    const isSelectedInList =
      selectedApplicant &&
      filteredAndSortedApplicants.some(
        (app) => app.id === selectedApplicant.id
      );

    // Determine if we should auto-select
    // Auto-select when:
    // 1. On 'applied' tab and no valid selection exists
    // 2. Selected applicant moved to a different stage (was selected but not in current list)
    const shouldAutoSelect =
      !isSelectedInList &&
      (activeTab === 'applied' || selectedApplicant !== null);

    // If no selection or selected applicant is not in current tab, select first one
    if (shouldAutoSelect) {
      if (isDesktop) {
        setSelectedApplicant(filteredAndSortedApplicants[0]);
        // Mark as read if it's unread
        if (filteredAndSortedApplicants[0].isUnread) {
          dispatch(markPitchAsRead(filteredAndSortedApplicants[0].id));
        }
      } else {
        // On mobile, clear selection when selected applicant is not in current tab
        setSelectedApplicant(null);
      }
    } else if (!isSelectedInList && activeTab !== 'applied') {
      // On non-applied tabs, if there's no valid selection and we didn't have a previous selection, just clear
      setSelectedApplicant(null);
    }
  }, [
    filteredAndSortedApplicants,
    activeTab,
    showHireModal,
    isDesktop,
    selectedApplicant,
    dispatch,
    selectedApplicantUserIdFromQuery,
  ]);

  // Handle mobile detail view visibility when tab changes
  useEffect(() => {
    if (!isDesktop) {
      // On mobile, return to list view when tab changes
      setShowMobileDetail(false);

      // Scroll active tab into view on mobile
      if (tabRefs.current[activeTab]) {
        tabRefs.current[activeTab].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeTab, isDesktop]);

  // Calculate tab counts
  const tabCounts = useMemo(() => {
    return {
      invited: invitedApplicants.length,
      applied: pitchApplicants.filter((a) => a.stage === 'applied').length,
      declined: pitchApplicants.filter((a) => a.stage === 'declined').length,
      reviewed: pitchApplicants.filter((a) => a.stage === 'reviewed').length,
      shortlisted: pitchApplicants.filter((a) => a.stage === 'shortlisted')
        .length,
      hired: pitchApplicants.filter((a) => a.stage === 'hired').length,
    };
  }, [invitedApplicants, pitchApplicants]);

  const getTabCount = (tabId) => {
    // Don't show counts while loading
    if (loadingPitches) {
      return '';
    }
    return tabCounts[tabId] || 0;
  };

  const prepareApplicationReviewWalkthroughStep = useCallback(
    (step) => {
      if (!firstAppliedApplicant) {
        return;
      }

      setActiveTab('applied');
      setUnreadFilter(false);
      setUnreadFilterApplicantIds([]);
      setSearchQuery('');
      setApplicantFilters((prevFilters) => {
        const selectedLocations = prevFilters?.selectedLocations || [];
        const selectedSkills = prevFilters?.selectedSkills || [];

        if (!selectedLocations.length && !selectedSkills.length) {
          return prevFilters;
        }

        return {
          ...prevFilters,
          selectedLocations: [],
          selectedSkills: [],
        };
      });
      setSelectedApplicant(firstAppliedApplicant);
      setShowMobileDetail(!isDesktop && step?.id !== 'applicant-card');
    },
    [firstAppliedApplicant, isDesktop]
  );

  const handleAtsWalkthroughStepChange = useCallback(
    (step) => {
      setWalkthroughActiveStepId(step?.id ?? null);

      if (!step || !APPLICATION_REVIEW_STEP_IDS.includes(step.id)) {
        return;
      }

      prepareApplicationReviewWalkthroughStep(step);
    },
    [prepareApplicationReviewWalkthroughStep]
  );

  useEffect(() => {
    if (atsWalkthroughManuallyOpened) {
      return;
    }

    const includesApplicationReviewTour = activeAtsTourFields.includes(
      ATS_POST_APPLICATIONS_TOUR
    );
    const shouldShowAtsWalkthrough =
      auth.user &&
      brief &&
      !isAdminViewingAsUser &&
      !loading &&
      pitchesLoaded &&
      activeAtsWalkthroughSteps.length > 0 &&
      !atsWalkthroughDismissed;

    if (!shouldShowAtsWalkthrough) {
      hasPreparedApplicationWalkthrough.current = false;
      setShowAtsWalkthrough(false);
      setWalkthroughActiveStepId(null);
      return;
    }

    if (
      includesApplicationReviewTour &&
      !hasPreparedApplicationWalkthrough.current
    ) {
      prepareApplicationReviewWalkthroughStep({ id: 'applicant-card' });
      hasPreparedApplicationWalkthrough.current = true;
    }

    setShowAtsWalkthrough(true);
  }, [
    activeAtsTourFields,
    activeAtsWalkthroughSteps.length,
    atsWalkthroughDismissed,
    atsWalkthroughManuallyOpened,
    auth.user,
    brief,
    isAdminViewingAsUser,
    loading,
    pitchesLoaded,
    prepareApplicationReviewWalkthroughStep,
  ]);

  useEffect(() => {
    if (!showAtsWalkthrough || !displayedAtsWalkthroughSteps.length) {
      hasTrackedAtsWalkthroughStart.current = false;
      return;
    }

    if (hasTrackedAtsWalkthroughStart.current) {
      return;
    }

    trackEvent(
      'client-ats-tour-started',
      getAtsWalkthroughTrackingProperties()
    );
    hasTrackedAtsWalkthroughStart.current = true;
  }, [
    displayedAtsWalkthroughSteps.length,
    getAtsWalkthroughTrackingProperties,
    showAtsWalkthrough,
  ]);

  // Find the full pitch data for the selected applicant - recomputes when selection changes
  // MUST be before early return to follow React hooks rules
  const selectedPitch = useMemo(() => {
    if (!selectedApplicant) return null;

    // For invited applicants, we don't need to create pitch data
    // ApplicantView will handle them directly via the applicant prop
    const isInvited = selectedApplicant.stage === 'invited';
    if (isInvited) {
      return null;
    }

    // Find the actual pitch
    return pitches.find((p) => p.id === selectedApplicant.id) || null;
  }, [selectedApplicant, pitches]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Create browse URL with job filters
  const browseURL = useMemo(() => {
    if (!brief) return '/browse';
    return getBrowseURL({
      role: brief.role,
      skill: brief.skill,
      ...(brief.remote ? {} : { location: brief.location }),
    });
  }, [brief]);

  const tabs = [
    { id: 'invited', label: 'Invited' },
    { id: 'applied', label: 'Applied' },
    { id: 'declined', label: 'Declined' },
    { id: 'reviewed', label: 'Reviewed' },
    { id: 'shortlisted', label: 'Shortlisted' },
    { id: 'hired', label: 'Hired' },
  ];

  // Early returns after all hooks
  if (!auth.loaded || loading || !brief) {
    return <Loader loading />;
  }

  // Redirect if user is not authenticated or not the owner
  if (auth.loaded && brief) {
    if (!auth.user) {
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        window.location.href = `/signin?referer=${encodeURIComponent(
          currentPath
        )}`;
      }
      return <Loader loading />;
    }

    const isOwner = brief.user && brief.user.id === auth.user.id;
    if (!isOwner) {
      if (typeof window !== 'undefined') {
        window.location.href = brief.links.main_relative;
      }
      return <Loader loading />;
    }
  }

  const isClosed = brief?.status === 'expired' || brief?.status === 'inactive';

  const briefStatus = brief?.status;
  const briefTitle = brief?.text;
  const briefRole = brief?.role;
  const postedDate = formatDate(brief?.timestamp);

  // Helper function to select next applicant after an action
  const selectNextApplicant = (currentIndex) => {
    // On mobile, return to list view instead of selecting next applicant
    if (!isDesktop) {
      setShowMobileDetail(false);
      setSelectedApplicant(null);
      return;
    }

    // Desktop behavior: select next applicant
    if (filteredAndSortedApplicants.length > 1) {
      // If there's a next applicant, select it
      if (currentIndex < filteredAndSortedApplicants.length - 1) {
        setSelectedApplicant(filteredAndSortedApplicants[currentIndex + 1]);
        // Mark as read if it's unread
        if (filteredAndSortedApplicants[currentIndex + 1].isUnread) {
          dispatch(
            markPitchAsRead(filteredAndSortedApplicants[currentIndex + 1].id)
          );
        }
      } else if (currentIndex > 0) {
        // Otherwise select the previous one
        setSelectedApplicant(filteredAndSortedApplicants[currentIndex - 1]);
        // Mark as read if it's unread
        if (filteredAndSortedApplicants[currentIndex - 1].isUnread) {
          dispatch(
            markPitchAsRead(filteredAndSortedApplicants[currentIndex - 1].id)
          );
        }
      } else {
        // No other applicants in this tab
        setSelectedApplicant(null);
      }
    } else {
      setSelectedApplicant(null);
    }
  };

  // Action handlers
  const handleDecline = () => {
    if (!selectedApplicant || !brief) return;

    trackEvent('client-ats-decline', {
      briefId: brief.id,
      pitchId: selectedApplicant.id,
    });

    // Find current applicant index in filtered list
    const currentIndex = filteredAndSortedApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch decline action
    dispatch(declinePitch(brief.id, selectedApplicant.id)).then(() => {
      selectNextApplicant(currentIndex);
    });
  };

  const handleShortlist = () => {
    if (!selectedApplicant || !brief) return;

    trackEvent('client-ats-shortlist', {
      briefId: brief.id,
      pitchId: selectedApplicant.id,
    });

    // Find current applicant index in filtered list
    const currentIndex = filteredAndSortedApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch shortlist action
    dispatch(shortlistPitch(brief.id, selectedApplicant.id)).then(() => {
      selectNextApplicant(currentIndex);
    });
  };

  const handleReview = () => {
    if (!selectedApplicant || !brief) return;

    trackEvent('client-ats-reviewed', {
      briefId: brief.id,
      pitchId: selectedApplicant.id,
    });

    // Find current applicant index in filtered list
    const currentIndex = filteredAndSortedApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch shortlist action
    dispatch(reviewPitch(brief.id, selectedApplicant.id)).then(() => {
      selectNextApplicant(currentIndex);
    });
  };

  const handleHire = () => {
    if (!selectedApplicant || !brief) return;
    setShowHireModal(true);
  };

  const handleAcceptPitchWithPayment = (totalToPay) => {
    if (!selectedApplicant || !brief || !selectedPitch) return;

    trackEvent('client-ats-payment-hire-made', {
      briefId: brief.id,
      pitchId: selectedPitch.id,
    });

    // Convert totalToPay to cents (totalToPay is in dollars/pounds, API expects cents/pence)
    const totalToPayInCents = totalToPay * 100;

    // Redirect to payment page, similar to the existing flow
    // The payment page will handle the actual payment processing
    history.push(
      `/manage/briefs/${brief.id}/pay/${
        selectedPitch.id
      }?amount=${totalToPayInCents}&isHire=true&returnTo=${encodeURIComponent(
        window.location.pathname
      )}`
    );
  };

  // eslint-disable-next-line no-unused-vars
  const handleAcceptPitchWithoutPayment = () => {
    if (!selectedApplicant || !brief) return;

    trackEvent('client-ats-no-payment-hire-made', {
      briefId: brief.id,
      pitchId: selectedApplicant.id,
    });

    // Find current applicant index in filtered list
    const currentIndex = filteredApplicants.findIndex(
      (app) => app.id === selectedApplicant.id
    );

    // Dispatch accept pitch action without payment (skip payment flow)
    dispatch(acceptPitch(brief.id, selectedApplicant.id)).then(() => {
      dispatch(
        reloadAuthAndTrackHireSuccess({
          briefId: brief.id,
          pitchId: selectedApplicant.id,
        })
      );
      // Don't close modal here - let the MarkedAsHired step show first
      // User will close it manually or via "Hire Others" button
      selectNextApplicant(currentIndex);
    });
  };

  const handleCloseHireModal = () => {
    setShowHireModal(false);
  };

  const handleEndHire = () => {
    if (!selectedApplicant || !brief) return;

    // Don't allow actions on dummy data
    if (selectedApplicant.isDummyData) {
      console.warn('Cannot end hire for dummy applicant');
      return;
    }

    // Dispatch end hire action and silently refresh pitches to get the review URL code
    dispatch(endHire(brief.id, selectedApplicant.id)).then(() => {
      // Silently refresh pitches to get fresh data with the review URL code
      // This won't trigger the loading spinner or tab count updates
      dispatch(refreshPitches(brief.id));
    });
  };

  const handleReset = (resetReviewedDate = true) => {
    if (!selectedApplicant || !brief) return;

    // Dispatch reset pitch action
    dispatch(resetPitch(brief.id, selectedApplicant.id, resetReviewedDate));
  };

  const handleLoadMessages = (username, pitchId, markAsRead = false) => {
    dispatch(loadPitchMessages(username, pitchId, markAsRead));
  };

  const handleLoadApplicantFeaturedProjects = (freelancerUsername) => {
    dispatch(loadApplicantFeaturedProjects(freelancerUsername));
  };

  const handleLoadMoreMessages = (pitchId) => {
    if (!selectedPitch) return;

    const username = selectedPitch.sender.username;
    const currentMeta = pitchMessagesMeta[pitchId] || { pagesLoaded: 0 };
    const nextPage = currentMeta.pagesLoaded + 1;

    dispatch(loadPitchMessages(username, pitchId, false, nextPage));
  };

  const handleMarkThreadAsRead = (username, pitchId) => {
    dispatch(markPitchThreadAsRead(username, pitchId));
  };

  const handleLoadPayments = (pitchId) => {
    dispatch(loadPitchPayments(pitchId));
  };

  const handleLoadInternalNotes = (freelancerId) => {
    dispatch(loadInternalNotes(freelancerId));
  };

  const handleSendMessage = (message) => {
    if (!selectedApplicant || !selectedPitch) {
      console.error('No applicant or pitch selected');
      return;
    }

    const recipient = selectedPitch.sender;
    const pitchId = selectedPitch.id;
    const currentUserId = auth.user?.id;

    dispatch(sendPitchMessage(recipient, message, pitchId, currentUserId));
  };

  const handleSendFile = (user, message) => {
    if (!selectedApplicant || !selectedPitch) {
      console.error('No applicant or pitch selected');
      return;
    }

    const pitchId = selectedPitch.id;
    const currentUserId = auth.user?.id;

    dispatch(sendPitchFile(user, message, pitchId, currentUserId));
  };

  const handleEditJobClick = (e) => {
    e.preventDefault();
    setShowJobMenu(false);
    setShowEditModal(true);
  };

  const handleDuplicateJobClick = (e) => {
    e.preventDefault();
    setShowJobMenu(false);
    setShowDuplicateModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
  };

  const handleSubmitDuplicateJob = (payload) => {
    setSavingBriefRequest(true);

    dispatch(createBrief(payload))
      .then(() => {
        setSavingBriefRequest(false);
        setShowDuplicateModal(false);
        trackEvent('client-copies-job', { briefId: brief?.id });
        dispatch(
          setSuccessMessage(
            'Your job has successfully been created and will be reviewed by the Twine team'
          )
        );
      })
      .catch((error) => {
        setSavingBriefRequest(false);
        console.error('Error duplicating brief:', error);
        // You could add error handling/notification here
      });
  };

  const handleSubmitEditJob = (payload) => {
    setSavingBriefRequest(true);

    dispatch(updateBrief(encodedId, payload))
      .then(() => {
        setSavingBriefRequest(false);
        setShowEditModal(false);
      })
      .catch((error) => {
        setSavingBriefRequest(false);
        console.error('Error updating brief:', error);
        // You could add error handling/notification here
      });
  };

  const handleToggleAIRecruiter = () => {
    const currentValue = aiRecruiterEnabled ?? brief?.ai_recruiter;
    const newValue = !currentValue;

    trackEvent('client-toggles-ai-recruiter', {
      briefId: brief?.id,
      enabled: newValue,
    });

    // Optimistically update local state for instant UI feedback
    setAiRecruiterEnabled(newValue);

    // Dispatch API call in background
    dispatch(updateBrief(encodedId, { ai_recruiter: newValue })).catch(
      (error) => {
        console.error('Error toggling AI recruiter:', error);
        // Revert on error
        setAiRecruiterEnabled(currentValue);
      }
    );
  };

  const handleTogglePrivate = () => {
    const currentValue = isPrivate ?? brief?.private;
    const newValue = !currentValue;

    // Optimistically update local state for instant UI feedback
    setIsPrivate(newValue);

    // Dispatch API call in background
    dispatch(updateBrief(encodedId, { private: newValue })).catch((error) => {
      console.error('Error toggling private status:', error);
      // Revert on error
      setIsPrivate(currentValue);
    });
  };

  const handleCloseJobClick = () => {
    setShowJobMenu(false);
    setShowCloseJobModal(true);
  };

  const handleCloseJobModalCancel = () => {
    setShowCloseJobModal(false);
  };

  const handleCloseJobConfirm = () => {
    if (!brief || closingJob) return;

    setClosingJob(true);
    dispatch(
      updateBrief(encodedId, {
        active: 0,
      })
    )
      .then(() => {
        setClosingJob(false);
        setShowCloseJobModal(false);
        trackEvent('client-closed-job', { briefId: brief?.id });
      })
      .catch((error) => {
        console.error('Failed to close job:', error);
        setClosingJob(false);
      });
  };

  const handleReopenJobClick = () => {
    setShowJobMenu(false);
    setShowReopenJobModal(true);
  };

  const handleReopenJobModalCancel = () => {
    setShowReopenJobModal(false);
  };

  const handleReopenJobConfirm = () => {
    if (!brief || reopeningJob) return;

    setReopeningJob(true);

    // Set expiry to 1 month from now
    const expires_at = format(
      new Date().setMonth(new Date().getMonth() + 1),
      'yyyy-MM-dd HH:mm:ss'
    );

    dispatch(
      updateBrief(encodedId, {
        active: 1,
        expires_at,
      })
    )
      .then(() => {
        setReopeningJob(false);
        setShowReopenJobModal(false);
      })
      .catch((error) => {
        console.error('Failed to re-open job:', error);
        setReopeningJob(false);
      });
  };

  const bulkActionConfig = {
    shortlisted: {
      title: 'Move to Shortlisted',
      message: `Are you sure you want to move ${
        selectedApplicantIds.length
      } applicant${
        selectedApplicantIds.length !== 1 ? 's' : ''
      } to shortlisted?`,
      confirmText: 'Move to Shortlisted',
      confirmStyle: 'primary',
      loading: bulkShortlistingPitches,
      action: () => bulkShortlistPitches(brief.id, selectedApplicantIds),
      successMessage: `${selectedApplicantIds.length} applicant${
        selectedApplicantIds.length !== 1 ? 's' : ''
      } moved to shortlisted`,
    },
    declined: {
      title: 'Mark as Not a Fit',
      message: `Are you sure you want to mark ${
        selectedApplicantIds.length
      } applicant${selectedApplicantIds.length !== 1 ? 's' : ''} as not a fit?`,
      confirmText: 'Not a Fit',
      confirmStyle: 'danger',
      loading: bulkDecliningPitches,
      action: () => bulkDeclinePitches(brief.id, selectedApplicantIds),
      successMessage: `${selectedApplicantIds.length} applicant${
        selectedApplicantIds.length !== 1 ? 's' : ''
      } marked as not a fit`,
    },
    reviewed: {
      title: 'Move to Reviewed',
      message: `Are you sure you want to move ${
        selectedApplicantIds.length
      } applicant${selectedApplicantIds.length !== 1 ? 's' : ''} to reviewed?`,
      confirmText: 'Move to Reviewed',
      confirmStyle: 'primary',
      loading: bulkReviewingPitches,
      action: () => bulkReviewPitches(brief.id, selectedApplicantIds),
      successMessage: `${selectedApplicantIds.length} applicant${
        selectedApplicantIds.length !== 1 ? 's' : ''
      } moved to reviewed`,
    },
    applied: {
      title: 'Move to Applied',
      message: `Are you sure you want to move ${
        selectedApplicantIds.length
      } applicant${selectedApplicantIds.length !== 1 ? 's' : ''} to applied?`,
      confirmText: 'Move to Applied',
      confirmStyle: 'primary',
      loading: bulkResettingPitches,
      action: () => bulkResetPitches(brief.id, selectedApplicantIds),
      successMessage: `${selectedApplicantIds.length} applicant${
        selectedApplicantIds.length !== 1 ? 's' : ''
      } moved to applied`,
    },
    'reset-reviewed': {
      title: 'Move to Reviewed',
      message: `Are you sure you want to move ${
        selectedApplicantIds.length
      } applicant${selectedApplicantIds.length !== 1 ? 's' : ''} to reviewed?`,
      confirmText: 'Move to Reviewed',
      confirmStyle: 'primary',
      loading: bulkResettingPitches,
      action: () => bulkResetPitches(brief.id, selectedApplicantIds, false),
      successMessage: `${selectedApplicantIds.length} applicant${
        selectedApplicantIds.length !== 1 ? 's' : ''
      } moved to reviewed`,
    },
  };

  const currentBulkAction = bulkActionType
    ? bulkActionConfig[bulkActionType]
    : null;

  const boostDaysRemaining = brief?.boosted_expires_at
    ? Math.ceil(
        (new Date(brief.boosted_expires_at).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24)
      )
    : 0;

  const isBoosted = boostDaysRemaining > 0;

  const {
    profileStepsCompleted,
    profileStepsTotal,
    profileCompletionPercentage,
    isProfileComplete,
  } = getProfileCompletion(auth.user);

  // Freelancer invite tracking
  const freelancersTarget = 10;
  const freelancersInvited = brief?.invites?.invites?.length || 0;

  const freelancerTargetHit = freelancersInvited >= freelancersTarget;

  const handleAtsWalkthroughClose = (eventName) => {
    trackEvent(eventName, getAtsWalkthroughTrackingProperties());
    setAtsWalkthroughDismissed(true);
    setShowAtsWalkthrough(false);
    setWalkthroughActiveStepId(null);
    hasPreparedApplicationWalkthrough.current = false;
    hasTrackedAtsWalkthroughStart.current = false;

    if (!auth.user) {
      return;
    }

    if (atsWalkthroughManuallyOpened || isAdminViewingAsUser) {
      setAtsWalkthroughManuallyOpened(false);
      return;
    }

    const completedTours = activeAtsTourFields.reduce(
      (tourData, tourField) => ({
        ...tourData,
        [tourField]: true,
      }),
      {}
    );

    dispatch(updateTour(auth.user, completedTours)).catch(() => {
      dispatch(setErrorMessage('Unable to save ATS tour progress'));
    });
  };

  const handleAtsWalkthroughComplete = () => {
    handleAtsWalkthroughClose('client-ats-tour-completed');
  };

  const handleAtsWalkthroughSkip = () => {
    handleAtsWalkthroughClose('client-ats-tour-skipped');
  };

  return (
    <Grid className={styles.wrapper}>
      <Row>
        <Col lg={12}>
          <div>
            <JobHeader
              briefTitle={briefTitle}
              briefStatus={briefStatus}
              briefRole={briefRole}
              postedDate={postedDate}
              isPrivate={isPrivate ?? brief?.private}
              isClosed={isClosed}
              showJobMenu={showJobMenu}
              onToggleMenu={(isOpen) => setShowJobMenu(isOpen)}
              onToggleWalkthrough={() => {
                trackEvent('client-ats-tour-restart-clicked', {
                  briefId: brief?.id,
                  hasAppliedApplicants,
                });
                setAtsWalkthroughManuallyOpened(true);
                setShowAtsWalkthrough(true);
              }}
              onEditJob={handleEditJobClick}
              onDuplicateJob={handleDuplicateJobClick}
              onCloseJob={handleCloseJobClick}
              onReopenJob={handleReopenJobClick}
              onTogglePrivate={handleTogglePrivate}
            />

            <JobActionCards
              publicQuestions={publicQuestions}
              isBoosted={isBoosted}
              boostDaysRemaining={boostDaysRemaining}
              isProfileComplete={isProfileComplete}
              profileStepsCompleted={profileStepsCompleted}
              profileStepsTotal={profileStepsTotal}
              profileCompletionPercentage={profileCompletionPercentage}
              freelancersInvited={freelancersInvited}
              freelancersTarget={freelancersTarget}
              freelancerTargetHit={freelancerTargetHit}
              browseURL={browseURL}
              aiRecruiterEnabled={aiRecruiterEnabled ?? brief?.ai_recruiter}
              onQuestionsClick={() => {
                trackEvent('client-ats-views-public-questions', {
                  briefId: brief?.id,
                });
                setShowQuestionsModal(true);
              }}
              onNextStepsClick={() => {
                trackEvent('client-ats-opens-next-steps');
                setShowNextStepsModal(true);
              }}
              onToggleAIRecruiter={handleToggleAIRecruiter}
            />

            <ApplicantTabs
              tabs={tabs}
              activeTab={activeTab}
              tabRefs={tabRefs}
              loadingPitches={loadingPitches}
              onTabChange={(tabId) => {
                setActiveTab(tabId);
                setSelectedApplicant(null);
                setShowMobileDetail(false);
                setHasChangedFilter(false);
                setUnreadFilter(false);
                setUnreadFilterApplicantIds([]);
                setSearchQuery('');
                setSelectedApplicantIds([]);
                setApplicantFilters({
                  sortOrder: 'newest',
                  selectedLocations: [],
                  selectedSkills: [],
                });
              }}
              getTabCount={getTabCount}
            />
          </div>
        </Col>
      </Row>

      <Row className={styles.content}>
        {/* Show empty state for no applicants */}
        {filteredAndSortedApplicants.length === 0 &&
        !loadingPitches &&
        (tabCounts[activeTab] === 0 || (!hasChangedFilter && !searchQuery)) ? (
          <Col xs={12}>
            <NoApplicantsEmptyState tab={activeTab} browseURL={browseURL} />
          </Col>
        ) : (
          <>
            <Col
              md={4}
              xs={12}
              className={`${styles.applicantColumn} ${
                showMobileDetail ? styles.hiddenMobile : styles.showMobile
              }`}
            >
              {loadingPitches ? (
                <Loader loading />
              ) : (
                <ApplicantList
                  applicants={filteredAndSortedApplicants}
                  selectedApplicantId={selectedApplicant?.id}
                  onSelectApplicant={(applicant) => {
                    setSelectedApplicant(applicant);
                    setShowMobileDetail(true);

                    // Mark as read if it's unread
                    if (applicant.isUnread) {
                      dispatch(markPitchAsRead(applicant.id));
                    }
                  }}
                  showInviteMoreButton={activeTab === 'invited'}
                  browseURL={browseURL}
                  unreadFilter={unreadFilter}
                  onToggleUnreadFilter={() => {
                    setUnreadFilter((prev) => !prev);
                    setUnreadFilterApplicantIds([]);
                    setHasChangedFilter(true);
                  }}
                  activeTab={activeTab}
                  searchQuery={searchQuery}
                  onSearchChange={(query) => {
                    setSearchQuery(query);
                    setHasChangedFilter(true);
                  }}
                  filters={applicantFilters}
                  onFiltersChange={(nextFilters) => {
                    setApplicantFilters(nextFilters);
                    setHasChangedFilter(true);
                  }}
                  sortOrder={applicantFilters?.sortOrder || 'newest'}
                  onSortOrderChange={(nextSortOrder) => {
                    setApplicantFilters((prev) => ({
                      ...prev,
                      sortOrder: nextSortOrder,
                    }));
                    setHasChangedFilter(true);
                  }}
                  availableLocations={availableLocations}
                  availableSkills={availableSkills}
                  selectedApplicantIds={
                    activeTab !== 'invited' ? selectedApplicantIds : []
                  }
                  onSelectAll={
                    activeTab !== 'invited'
                      ? () => {
                          const filteredIds = filteredAndSortedApplicants.map(
                            (a) => a.id
                          );
                          setSelectedApplicantIds((prev) =>
                            prev.length === filteredIds.length
                              ? []
                              : filteredIds
                          );
                        }
                      : undefined
                  }
                  showSelectAll={activeTab !== 'invited'}
                  onToggleSelection={
                    activeTab !== 'invited'
                      ? (applicantId) => {
                          setSelectedApplicantIds((prev) =>
                            prev.includes(applicantId)
                              ? prev.filter((id) => id !== applicantId)
                              : [...prev, applicantId]
                          );
                        }
                      : undefined
                  }
                />
              )}
            </Col>
            <Col
              md={8}
              xs={12}
              className={`${styles.detailsColumn} ${
                showMobileDetail ? styles.showMobile : styles.hiddenMobile
              }`}
            >
              {selectedApplicant ? (
                <>
                  {/* Mobile Back Button */}
                  <button
                    onClick={() => setShowMobileDetail(false)}
                    className={styles.mobileBackButton}
                  >
                    <ArrowLeft width={20} height={20} />
                    <span>Back to list</span>
                  </button>

                  <ApplicantView
                    pitch={selectedPitch}
                    user={selectedApplicant.user}
                    applicantStage={selectedApplicant.stage}
                    onShortlist={handleShortlist}
                    onReview={handleReview}
                    onDecline={handleDecline}
                    onHire={handleHire}
                    onEndHire={handleEndHire}
                    onReset={handleReset}
                    decliningPitch={decliningPitch}
                    shortlistingPitch={shortlistingPitch}
                    reviewingPitch={reviewingPitch}
                    endingHire={endingHire}
                    resettingPitch={resettingPitch}
                    hiringPitch={acceptingPitch}
                    currentUser={auth.user}
                    pitchMessages={pitchMessages}
                    pitchMessagesMeta={pitchMessagesMeta}
                    loadingPitchMessages={loadingPitchMessages}
                    markingThreadAsRead={markingThreadAsRead}
                    markedThreads={markedThreads}
                    onLoadMessages={handleLoadMessages}
                    onLoadMoreMessages={handleLoadMoreMessages}
                    onSendMessage={handleSendMessage}
                    onSendFile={handleSendFile}
                    onMarkThreadAsRead={handleMarkThreadAsRead}
                    pitchPayments={pitchPayments}
                    pitchPaymentsStats={pitchPaymentsStats}
                    loadingPitchPayments={loadingPitchPayments}
                    onLoadPayments={handleLoadPayments}
                    briefEncodedId={match.params.id}
                    brief={brief}
                    internalNotes={internalNotes}
                    loadingInternalNotes={loadingInternalNotes}
                    onLoadInternalNotes={handleLoadInternalNotes}
                    onLoadApplicantFeaturedProjects={
                      handleLoadApplicantFeaturedProjects
                    }
                    showPaymentsPlaceholderTab={
                      walkthroughActiveStepId === 'payments-tab'
                    }
                    initialApplicantTab={applicantTabFromQuery}
                  />
                </>
              ) : filteredApplicants.length === 0 ? (
                <NoApplicantsEmptyState tab="filtered" />
              ) : (
                <NoApplicantsEmptyState tab="noSelected" />
              )}
            </Col>
          </>
        )}
      </Row>

      {/* Bulk Action Bar */}

      <BulkActionBar
        selectedCount={selectedApplicantIds.length}
        currentStage={activeTab}
        onClearSelection={() => setSelectedApplicantIds([])}
        onMessage={() => setShowBulkMessageModal(true)}
        onMoveToStage={(stage) => {
          setBulkActionType(stage);
        }}
      />

      {/* Bulk Message Modal */}
      <BulkMessageModal
        show={showBulkMessageModal}
        onHide={() => setShowBulkMessageModal(false)}
        selectedApplicants={filteredAndSortedApplicants.filter((applicant) =>
          selectedApplicantIds.includes(applicant.id)
        )}
        onSendMessage={(message) => {
          const selectedApplicants = filteredAndSortedApplicants.filter(
            (applicant) => selectedApplicantIds.includes(applicant.id)
          );
          dispatch(sendBulkMessages(selectedApplicants, message, auth.user.id));
          setShowBulkMessageModal(false);
          setSelectedApplicantIds([]);
          dispatch(
            setSuccessMessage(
              `Message sent to ${selectedApplicants.length} applicant${
                selectedApplicants.length !== 1 ? 's' : ''
              }`
            )
          );
        }}
        sending={sendingBulkMessages}
      />

      {/* Bulk Action Confirmation Modal */}
      {currentBulkAction && (
        <BulkConfirmationModal
          show={!!bulkActionType}
          onHide={() => setBulkActionType(null)}
          title={currentBulkAction.title}
          message={currentBulkAction.message}
          confirmText={currentBulkAction.confirmText}
          confirmStyle={currentBulkAction.confirmStyle}
          loading={currentBulkAction.loading}
          onConfirm={() => {
            dispatch(currentBulkAction.action()).then(() => {
              setBulkActionType(null);
              setSelectedApplicantIds([]);
              dispatch(setSuccessMessage(currentBulkAction.successMessage));
            });
          }}
        />
      )}

      {/* Public Questions Modal */}
      <PublicQuestionsModal
        show={showQuestionsModal}
        onHide={() => setShowQuestionsModal(false)}
        questions={publicQuestions}
        briefId={brief?.id}
      />

      {/* Hire Modal with PaymentModal */}
      {selectedPitch && (
        <Modal
          closeButton
          show={showHireModal}
          title={`Hire ${
            selectedPitch.sender?.displayname || selectedPitch.sender?.username
          }`}
          onExit={handleCloseHireModal}
        >
          <PaymentModal
            brief={brief}
            pitch={selectedPitch}
            onAccept={handleAcceptPitchWithPayment}
            onAcceptPitchOverride={handleAcceptPitchWithoutPayment}
            closeModal={handleCloseHireModal}
            hireOthers={handleCloseHireModal}
          />
        </Modal>
      )}

      {/* Edit Job Modal */}
      <EditJobModal
        brief={brief}
        show={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleSubmitEditJob}
        saving={savingBriefRequest}
      />

      {/* Duplicate Job Modal */}
      <DuplicateJobModal
        brief={brief}
        show={showDuplicateModal}
        onClose={handleCloseDuplicateModal}
        onSubmit={handleSubmitDuplicateJob}
        saving={savingBriefRequest}
        publicQuestions={publicQuestions}
      />

      <CloseJobModal
        show={showCloseJobModal}
        onCancel={handleCloseJobModalCancel}
        onConfirm={handleCloseJobConfirm}
        isClosing={closingJob}
      />

      <ReopenJobModal
        show={showReopenJobModal}
        onCancel={handleReopenJobModalCancel}
        onConfirm={handleReopenJobConfirm}
        isReopening={reopeningJob}
      />

      <NextStepsModal
        show={showNextStepsModal}
        onHide={() => setShowNextStepsModal(false)}
      />

      <Walkthrough
        isOpen={showAtsWalkthrough}
        steps={displayedAtsWalkthroughSteps}
        onComplete={handleAtsWalkthroughComplete}
        onSkip={handleAtsWalkthroughSkip}
        onStepChange={handleAtsWalkthroughStepChange}
      />
    </Grid>
  );
};

JobDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

export default asyncConnect([
  {
    promise: ({
      store: { dispatch, getState },
      match: {
        params: { id },
      },
      location,
    }) => {
      const encodedId = id ? id.split('-')[0] : null;
      const queryParams = new URLSearchParams(location?.search || '');
      const selectedApplicantUserId = queryParams.get('selectedApplicant');
      const initialState = getState();
      const currentBrief = initialState.clientATS?.brief;

      // Load if no brief exists OR if the loaded brief doesn't match the requested ID
      if (encodedId && (!currentBrief || currentBrief.id !== encodedId)) {
        return dispatch(loadBrief(encodedId)).then(() => {
          if (!selectedApplicantUserId) {
            return Promise.resolve();
          }

          const loadedState = getState();
          const loadedBrief = loadedState.clientATS?.brief;
          const authUserId = loadedState.auth?.user?.id;
          const briefOwnerId = loadedBrief?.user?.id;

          if (authUserId && briefOwnerId && authUserId === briefOwnerId) {
            return dispatch(loadPitches(encodedId));
          }

          return Promise.resolve();
        });
      }

      if (encodedId && selectedApplicantUserId) {
        const authUserId = initialState.auth?.user?.id;
        const briefOwnerId = currentBrief?.user?.id;

        if (authUserId && briefOwnerId && authUserId === briefOwnerId) {
          return dispatch(loadPitches(encodedId));
        }
      }

      return Promise.resolve();
    },
  },
])(JobDetails);
